import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";

/**
 * /rewire-deck — Coming Soon + Waitlist for the REWIRE deck
 * Tagline: "Same brain. New path."
 * Theme: Neuroplasticity self-regulation tools · Mind • Body • Spirit • Hygiene™
 * Price: $2.22 (impulse digital download tier)
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WAITLIST_GROUP = "rewire-deck-waitlist";

const BRAND = {
  teal: "#068fa2",
  tealDeep: "#0a5b6a",
  tealInk: "#042f2e",
  cream: "#efeae7",
  gold: "#d4af6a",
  orange: "#e8a06b",
};

// Visible MIND category cards from the source PDF — used as sneak peek list
const SNEAK_PEEK_CARDS = [
  { category: "MIND", title: "The Inner Critic Loop" },
  { category: "MIND", title: "The Catastrophe Spiral" },
  { category: "MIND", title: "The Comparison Scroll" },
  { category: "MIND", title: "The Rumination Replay" },
  { category: "MIND", title: 'The "I\u2019m Behind" Story' },
  { category: "MIND", title: "The All-or-Nothing Trap" },
  { category: "MIND", title: "The Doom Scroll Sunrise" },
];

export default function RewireDeckPage() {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === "submitting") return;
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/api/subscribe`, {
        email: email.trim().toLowerCase(),
        firstName: firstName.trim() || "Friend",
        group_id: WAITLIST_GROUP,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail ||
          "Something hiccupped. Please try again in a moment."
      );
    }
  };

  return (
    <div
      data-testid="rewire-deck-page"
      style={{ backgroundColor: BRAND.cream, minHeight: "100vh", color: BRAND.tealInk }}
    >
      <Helmet>
        <title>REWIRE Card Deck — Same Brain. New Path. | Neuroplasticity Tools by Temple Franklin</title>
        <meta
          name="description"
          content="A deck for catching the spiral and choosing differently. Each card walks you OLD GROOVE → NEW PATH → REMEMBER. Real tools, real time, in your hands. Digital deck $2.22. Available now."
        />
        <meta name="keywords" content="REWIRE deck, neuroplasticity cards, brain rewiring tools, cognitive distortion cards, self-regulation deck, Temple Franklin, Mind Body Spirit HYGIENE™, anxiety tools" />
        <link rel="canonical" href="https://mindbodyspirithygienetools.com/rewire-deck" />
        <meta property="og:type" content="product" />
        <meta property="og:title" content="REWIRE Card Deck — Same Brain. New Path." />
        <meta property="og:description" content="A deck for catching the spiral and choosing differently. Old Groove → New Path → Remember. $2.22 digital deck. Available now." />
        <meta property="og:image" content="https://mindbodyspirithygienetools.com/rewire-deck/cover-card.png" />
        <meta property="og:url" content="https://mindbodyspirithygienetools.com/rewire-deck" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "REWIRE Card Deck",
          "description": "A deck for catching the spiral and choosing differently. OLD GROOVE → NEW PATH → REMEMBER. Real tools, real time. Digital download.",
          "brand": {"@type": "Brand", "name": "Mind Body Spirit HYGIENE\u2122"},
          "image": "https://mindbodyspirithygienetools.com/rewire-deck/cover-card.png",
          "offers": {"@type": "Offer", "price": "2.22", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "url": "https://mindbodyspirithygienetools.com/rewire-deck"}
        })}</script>
      </Helmet>
      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="px-6 pt-16 pb-12 md:pt-20 md:pb-16 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          {/* Left — Copy */}
          <div>
            <div
              data-testid="coming-soon-badge"
              className="inline-block uppercase text-xs md:text-sm px-5 py-2 rounded-full mb-6"
              style={{
                backgroundColor: BRAND.teal,
                color: BRAND.cream,
                letterSpacing: "0.3em",
              }}
            >
              ✦ Live Now — $2.22 ✦
            </div>

            <h1
              className="text-5xl sm:text-6xl lg:text-7xl mb-4 font-black tracking-tight"
              style={{ color: BRAND.tealDeep, letterSpacing: "-0.02em" }}
            >
              REWIRE
            </h1>

            <div
              className="h-1 w-24 mb-5"
              style={{ backgroundColor: BRAND.gold }}
              aria-hidden="true"
            />

            <p
              className="text-2xl md:text-3xl italic mb-2"
              style={{ color: BRAND.tealDeep, fontFamily: "Georgia, serif" }}
            >
              Same brain. New path.
            </p>

            <p
              className="text-base md:text-lg italic mb-7 opacity-80"
              style={{ color: BRAND.tealDeep }}
            >
              Mind &middot; Body &middot; Spirit &middot; Hygiene&trade; Tools
            </p>

            <p
              className="text-base md:text-lg leading-relaxed mb-7"
              style={{ color: BRAND.tealInk }}
            >
              A deck for catching the spiral and choosing differently.{" "}
              <strong>Real tools. Real time. In your hands.</strong> Each card
              names the old groove your brain is stuck in, hands you a tested
              new path, and gives you one line to remember when you need it
              most.
            </p>

            <p
              className="text-sm md:text-base mb-2 uppercase tracking-[0.2em]"
              style={{ color: BRAND.gold }}
            >
              Launch price
            </p>
            <p
              className="text-5xl md:text-6xl mb-1"
              style={{ color: BRAND.tealDeep, fontFamily: "Georgia, serif", fontWeight: 700 }}
            >
              $2.22
            </p>
            <p className="text-sm italic mb-8" style={{ opacity: 0.7 }}>
              Digital download. Print at home or use on your phone.
            </p>

            <a
              href="https://templefranklin22.gumroad.com/l/vakzb"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-buy-cta"
              className="inline-block px-8 py-4 rounded-md uppercase tracking-[0.2em] text-sm md:text-base transition-opacity hover:opacity-90"
              style={{
                backgroundColor: BRAND.tealDeep,
                color: BRAND.cream,
                fontWeight: 600,
              }}
            >
              Buy · $2.22 &rarr;
            </a>
          </div>

          {/* Right — Cover card image */}
          <div className="flex justify-center">
            <div
              className="rounded-lg overflow-hidden shadow-2xl max-w-[380px] w-full"
              style={{ border: `1px solid ${BRAND.gold}50` }}
            >
              <img
                src="/rewire-deck/cover-card.png"
                alt="REWIRE deck cover card"
                className="w-full h-auto block"
                data-testid="rewire-cover-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY IT WORKS (NEUROPLASTICITY) ───────────────── */}
      <section
        className="px-6 py-16 md:py-20"
        style={{ backgroundColor: BRAND.tealDeep, color: BRAND.cream }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <p
            className="uppercase tracking-[0.3em] text-xs mb-3"
            style={{ color: BRAND.gold }}
          >
            ✦ Why it works ✦
          </p>
          <h2
            className="text-3xl md:text-4xl mb-6"
            style={{ fontFamily: "Georgia, serif" }}
          >
            How rewiring works.
          </h2>
          <p className="text-base md:text-lg leading-relaxed opacity-95">
            Neuroplasticity is the brain&rsquo;s ability to form new pathways at
            any age.{" "}
            <em>You are not your old patterns.</em> Every time you catch a
            spiral and choose a different response, you are practising the new
            pathway. This deck gives you the exact catch-points and the
            exact next steps &mdash; in 30 seconds or less.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mt-12 text-left">
            <div>
              <p
                className="text-3xl mb-2"
                style={{ color: BRAND.gold, fontFamily: "Georgia, serif" }}
              >
                01
              </p>
              <p
                className="uppercase tracking-[0.2em] text-xs mb-2"
                style={{ color: BRAND.gold }}
              >
                The Old Groove
              </p>
              <p className="text-sm opacity-90 leading-relaxed">
                Each card names the exact looping thought pattern your brain
                slips into &mdash; so you can finally see it.
              </p>
            </div>
            <div>
              <p
                className="text-3xl mb-2"
                style={{ color: BRAND.gold, fontFamily: "Georgia, serif" }}
              >
                02
              </p>
              <p
                className="uppercase tracking-[0.2em] text-xs mb-2"
                style={{ color: BRAND.gold }}
              >
                The New Path
              </p>
              <p className="text-sm opacity-90 leading-relaxed">
                A tested, body-based self-regulation tool you can do in the
                moment &mdash; standing in line, mid-meeting, 3 AM.
              </p>
            </div>
            <div>
              <p
                className="text-3xl mb-2"
                style={{ color: BRAND.gold, fontFamily: "Georgia, serif" }}
              >
                03
              </p>
              <p
                className="uppercase tracking-[0.2em] text-xs mb-2"
                style={{ color: BRAND.gold }}
              >
                Remember
              </p>
              <p className="text-sm opacity-90 leading-relaxed">
                One line to anchor it &mdash; what to whisper to yourself
                tomorrow when the same pattern comes knocking.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SNEAK PEEK ───────────────────────────────────── */}
      <section className="px-6 py-16 max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p
            className="uppercase tracking-[0.3em] text-xs mb-2"
            style={{ color: BRAND.gold }}
          >
            ✦ Sneak Peek ✦
          </p>
          <h2
            className="text-2xl md:text-3xl"
            style={{ fontFamily: "Georgia, serif", color: BRAND.tealDeep }}
          >
            A few cards from the MIND deck.
          </h2>
          <p className="text-sm md:text-base mt-3 italic" style={{ opacity: 0.7 }}>
            (The full deck covers MIND, BODY, SPIRIT, and HYGIENE &mdash; this
            is just a taste.)
          </p>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="sneak-peek-grid"
        >
          {SNEAK_PEEK_CARDS.map((c, i) => (
            <div
              key={c.title}
              data-testid={`peek-card-${i}`}
              className="rounded-lg overflow-hidden shadow-md transition-transform duration-300 hover:scale-[1.03] hover:shadow-xl"
              style={{
                backgroundColor: BRAND.teal,
                color: BRAND.cream,
                aspectRatio: "5 / 7",
                position: "relative",
              }}
            >
              <div
                className="absolute top-0 right-0 w-20 h-20 rounded-bl-full"
                style={{ backgroundColor: BRAND.tealInk }}
                aria-hidden="true"
              />
              <div className="p-5 h-full flex flex-col justify-between">
                <div>
                  <p className="text-xs tracking-[0.3em] opacity-90">REWIRE</p>
                </div>
                <div>
                  <p
                    className="uppercase text-xs tracking-[0.3em] mb-2"
                    style={{ color: BRAND.gold }}
                  >
                    {c.category}
                  </p>
                  <p
                    className="text-xl md:text-2xl leading-tight"
                    style={{ fontFamily: "Georgia, serif" }}
                  >
                    {c.title}
                  </p>
                  <div
                    className="h-px mt-4"
                    style={{ backgroundColor: `${BRAND.gold}80` }}
                  />
                </div>
                <p className="text-xs opacity-75 italic">
                  Mind &middot; Body &middot; Spirit &middot; Hygiene&trade;
                </p>
              </div>
            </div>
          ))}
        </div>

        <p
          className="text-center text-sm italic mt-10"
          style={{ color: BRAND.tealDeep, opacity: 0.7 }}
        >
          The rest of the deck stays sealed until launch day.
        </p>
      </section>

      {/* ── WAITLIST ─────────────────────────────────────── */}
      <section
        id="waitlist"
        className="px-6 py-16 md:py-20"
        style={{ backgroundColor: BRAND.cream }}
      >
        <div
          className="max-w-xl mx-auto p-8 md:p-12 rounded-lg text-center"
          style={{
            backgroundColor: BRAND.tealDeep,
            color: BRAND.cream,
            boxShadow: "0 20px 60px rgba(4,47,46,0.15)",
          }}
        >
          {status === "success" ? (
            <div data-testid="waitlist-success">
              <p
                className="uppercase tracking-[0.25em] text-xs mb-3"
                style={{ color: BRAND.gold }}
              >
                ✦ You&rsquo;re on the list ✦
              </p>
              <h3
                className="text-3xl md:text-4xl mb-5"
                style={{ fontFamily: "Georgia, serif" }}
              >
                Welcome home.
              </h3>
              <p className="text-base leading-relaxed opacity-90">
                I&rsquo;ll send you the launch link the moment the deck goes
                live. First 100 on the list get a launch-day discount and a
                printable &ldquo;How to Use Your Deck&rdquo; guide.
              </p>
              <p className="text-sm italic mt-6 opacity-75">&mdash; Temple</p>
            </div>
          ) : (
            <>
              <p
                className="uppercase tracking-[0.25em] text-xs mb-3"
                style={{ color: BRAND.gold }}
              >
                ✦ Join the waitlist ✦
              </p>
              <h3
                className="text-3xl md:text-4xl mb-3"
                style={{ fontFamily: "Georgia, serif" }}
              >
                Be first to rewire.
              </h3>
              <p className="text-base mb-7 opacity-90 leading-relaxed">
                Launches NOW at <strong>$2.22</strong>. First 100 on
                the list get a launch-day discount.
              </p>

              <form
                onSubmit={handleSubmit}
                className="space-y-3"
                data-testid="waitlist-form"
              >
                <input
                  data-testid="waitlist-firstname"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name (optional)"
                  className="w-full px-5 py-3 rounded-md text-base"
                  style={{
                    backgroundColor: BRAND.cream,
                    color: BRAND.tealInk,
                    border: "none",
                  }}
                />
                <input
                  data-testid="waitlist-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="w-full px-5 py-3 rounded-md text-base"
                  style={{
                    backgroundColor: BRAND.cream,
                    color: BRAND.tealInk,
                    border: "none",
                  }}
                />
                <button
                  data-testid="waitlist-submit"
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full px-6 py-3 rounded-md text-base uppercase tracking-[0.2em] transition-opacity disabled:opacity-60"
                  style={{
                    backgroundColor: BRAND.gold,
                    color: BRAND.tealInk,
                    fontWeight: 600,
                  }}
                >
                  {status === "submitting"
                    ? "Adding you…"
                    : "Reserve my spot"}
                </button>
                {status === "error" && (
                  <p
                    data-testid="waitlist-error"
                    className="text-sm mt-2"
                    style={{ color: "#ffd6c2" }}
                  >
                    {errorMsg}
                  </p>
                )}
              </form>

              <p className="text-xs italic mt-5 opacity-70">
                No spam. Unsubscribe anytime. Just the launch + the discount.
              </p>
            </>
          )}
        </div>
      </section>

      {/* ── FOOTER LINKS ─────────────────────────────────── */}
      <section className="px-6 py-10 text-center text-sm" style={{ opacity: 0.75 }}>
        <p>
          Also in the works:{" "}
          <Link
            to="/i-get-to-deck"
            data-testid="link-i-get-to-deck"
            style={{ color: BRAND.teal, textDecoration: "underline" }}
          >
            the I GET TO deck
          </Link>{" "}
          &middot;{" "}
          <Link
            to="/book"
            data-testid="link-book"
            style={{ color: BRAND.teal, textDecoration: "underline" }}
          >
            the book
          </Link>{" "}
          &middot;{" "}
          <Link
            to="/draw"
            data-testid="link-free-card"
            style={{ color: BRAND.teal, textDecoration: "underline" }}
          >
            pull a free card
          </Link>
          .
        </p>
        <p className="mt-3 italic" style={{ opacity: 0.7 }}>
          Mind Body Spirit HYGIENE&trade; &middot; mindbodyspirithygiene.com
        </p>
      </section>
    </div>
  );
}
